import React, { useCallback, FC }      from 'react';
import styled                          from 'styled-components';
import {
  TDataTableRow,
  TSelectOnChange,
}                                      from '@typings/components';
import {
  IComment,
  IDocument,
  ICommentValues,
}                                      from '@typings/comments';
import {
  IBidderUser,
  IUpdateBidderPassPayload,
}                                      from '@typings/registeredBidders';
import { IResetPasswordInitialValues } from '@typings/auth';
import { EAuthPage }                   from '@enums/auth';
import {
  initialValues,
  validationSchema,
}                                      from '@modules/auth/pages/AuthPage';
import {
  USERS_ACTIVITY_TABLE_HEADERS,
  COMPLAINT_USERS_TABLE_HEADERS,
}                                      from '@modules/registeredBidders/utils/tableHeaders';
import {
  DataTable,
  LinkButton,
  LinksWrapper,
  CommentForm,
  CommentsList,
}                                      from '@components/index';
import { ResetPassword }               from '@modules/auth/components/ResetPassword';
import { UserDetails }                 from '@modules/registeredBidders/components/UserDetails';

const LineWrapper = styled.div`
  display       : flex;
  align-items   : center;
  width         : 800px;
  padding       : 5px;
  margin-bottom : 5px;

  span {
    font-size : 16px;
  }

    > span:first-child {
    flex-basis  : 160px;
    text-align  : right;
    white-space : nowrap;
  }

    > span:last-child {
    flex-basis  : 200px;
    margin-left : 15px;
  }
`;

const Title = styled.span`
  margin-top    : 10px;
  margin-bottom : 15px;
  font-size     : 20px !important;
  font-weight   : 500;
  color         : ${({ theme: { textColor } }): string => textColor.secondary} !important;
`;

const UpdatePassWrapper = styled(LineWrapper)`
  margin-top: 20px;
  margin-left: 175px;
`;

interface IProps {
  user                 : IBidderUser;
  comments             : IComment[];
  bids                 : TDataTableRow[];
  isPasswordUpdating   : boolean;
  complaints           : TDataTableRow[];
  fetchDocument        : (document: IDocument) => Promise<void>;
  handleSaveComment    : (comment: ICommentValues) => Promise<void>;
  handleChangeStatus   : TSelectOnChange;
  handleClickReturn    : () => void;
  updateBidderPassword : (payload: IUpdateBidderPassPayload) => Promise<void>;
}

export const BidderDetailsPage: FC<IProps> = ({
  user,
  comments,
  bids,
  isPasswordUpdating,
  complaints,
  fetchDocument,
  handleSaveComment,
  handleChangeStatus,
  handleClickReturn,
  updateBidderPassword,
}) => {
  const handleResetPassword = useCallback(
    async ({ newPassword }: IResetPasswordInitialValues): Promise<void> => {
      const { id: userId } = user;

      await updateBidderPassword({ userId, newPassword });
    },
    [updateBidderPassword, user],
  );

  return (
    <>
      <LinksWrapper>
        <LinkButton
          label   = "Return to Registered Bidders"
          onClick = {handleClickReturn}
        />
      </LinksWrapper>

      <LineWrapper>
        <span />
        <Title>Info</Title>
      </LineWrapper>

      <UserDetails
        user           = {user}
        onChangeStatus = {handleChangeStatus}
      />

      <LineWrapper>
        <span />
        <Title>Comments</Title>
      </LineWrapper>

      <CommentForm handleSaveComment={handleSaveComment} />

      <CommentsList
        comments      = {comments}
        fetchDocument = {fetchDocument}
      />

      {!!bids.length && (
        <>
          <LineWrapper>
            <span />
            <Title>Activity</Title>
          </LineWrapper>

          <DataTable
            headers  = {USERS_ACTIVITY_TABLE_HEADERS}
            rows     = {bids}
            maxwidth = "700px"
            margin   = "0 0 20px 180px"
          />
        </>
      )}

      {!!complaints.length && (
        <>
          <LineWrapper>
            <span />
            <Title>Complaints</Title>
          </LineWrapper>

          <DataTable
            headers  = {COMPLAINT_USERS_TABLE_HEADERS}
            rows     = {complaints}
            maxwidth = "700px"
            margin   = "0 0 20px 180px"
          />
        </>
      )}

      <UpdatePassWrapper>
        <ResetPassword
          isPasswordResetting = {isPasswordUpdating}
          initialValues       = {initialValues[EAuthPage.ResetPass]}
          validationSchema    = {validationSchema[EAuthPage.ResetPass]}
          handleResetPassword = {handleResetPassword}
          isUpdateBidderPass
        />
      </UpdatePassWrapper>
    </>
  );
};
